import { Button, StatusProxy } from "@focus/focusUI";
import { useEffect, useMemo } from "react";
import { type ContactsByOperatorSelection } from "../ContactsByOperatorButton";
import { usePhonebook } from "../../hooks/usePhonebook";
import type { Contact } from "../../types/phonebook";

interface ConjunctionContactsPanelProps {
  className?: string;
  selection: ContactsByOperatorSelection;
  onClose: () => void;
  variant?: "card" | "embedded";
}

const emptyValue = "-";

const displayValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") return emptyValue;
  return String(value);
};

const getContactInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return "?";

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

const ContactField = ({
  label,
  value,
  href,
}: {
  label: string;
  value?: string;
  href?: string;
}) => {
  const content = displayValue(value);
  const className = `truncate text-sm font-semibold ${
    content === emptyValue ? "text-slate-400" : "text-slate-700"
  }`;

  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase text-slate-400">
        {label}
      </div>
      {href && content !== emptyValue ? (
        <a
          className={`${className} block transition hover:text-slate-950 hover:no-underline`}
          href={href}
          title={content}
        >
          {content}
        </a>
      ) : (
        <div className={className} title={content}>
          {content}
        </div>
      )}
    </div>
  );
};

const ContactCard = ({ contact }: { contact: Contact }) => {
  const name = displayValue(contact.contactName);
  const phoneNumber = displayValue(contact.phoneNumber);
  const email = displayValue(contact.email);

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex min-w-0 items-start gap-3">
        <div className="focusoc-theme-surface flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
          {getContactInitials(name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-slate-950" title={name}>
            {name}
          </div>
          <div
            className="mt-0.5 truncate text-xs font-semibold text-slate-500"
            title={displayValue(contact.role)}
          >
            {displayValue(contact.role)}
          </div>
        </div>
      </div>

      <div className="mt-3 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
        <ContactField
          href={
            phoneNumber === emptyValue
              ? undefined
              : `tel:${phoneNumber.replace(/\s/g, "")}`
          }
          label="Phone"
          value={contact.phoneNumber}
        />
        <ContactField
          href={email === emptyValue ? undefined : `mailto:${email}`}
          label="Email"
          value={contact.email}
        />
      </div>

      {contact.notes && (
        <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
          {contact.notes}
        </div>
      )}
    </article>
  );
};

export const ConjunctionContactsPanel: React.FC<
  ConjunctionContactsPanelProps
> = ({ className = "", selection, onClose, variant = "embedded" }) => {
  const {
    contactsByOperator,
    getOrganizationMembers,
    getRequestStatus,
    getRequestError,
  } = usePhonebook();

  useEffect(() => {
    getOrganizationMembers(selection.operator);
  }, [
    selection.operator,
    selection.rowId,
    selection.eventId,
    selection.targetName,
    selection.chaserName,
    selection.timeOfCloseApproach,
  ]);

  const contacts = useMemo(
    () => contactsByOperator[selection.operator] ?? [],
    [contactsByOperator, selection.operator],
  );

  const status = getRequestStatus("getContactsByOperatorName");
  const error = getRequestError("getContactsByOperatorName");

  const containerClassName =
    variant === "card"
      ? "flex h-full min-h-0 w-[340px] flex-none flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
      : "flex h-full min-h-0 w-[320px] flex-none flex-col overflow-hidden border-r border-slate-200 bg-white";

  return (
    <aside className={`${containerClassName} ${className}`}>
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-4">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-slate-500">
            Phonebook contacts
          </div>
          <h2
            className="mt-1 truncate text-lg font-bold text-slate-950"
            title={selection.operator}
          >
            {selection.operator}
          </h2>
          <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold text-slate-500">
            <span>{displayValue(selection.targetName)}</span>
            <span className="text-slate-300">/</span>
            <span>{displayValue(selection.chaserName)}</span>
          </div>
        </div>

        <Button
          className="focusoc-icon-button"
          icon="cross"
          minimal
          onClick={onClose}
          title="Close contacts"
        />
      </header>

      <div className="min-h-0 flex-1 overflow-hidden">
        <StatusProxy status={status} error={error}>
          {contacts.length > 0 ? (
            <div className="flex h-full min-h-0 flex-col gap-2.5 overflow-y-auto bg-slate-50/50 p-3">
              {contacts.map((contact, index) => (
                <ContactCard
                  contact={contact}
                  key={contact.id ?? `${contact.contactName}-${index}`}
                />
              ))}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm font-semibold text-slate-500">
              No contacts found for this organization.
            </div>
          )}
        </StatusProxy>
      </div>
    </aside>
  );
};
